import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Play, Check, X, Camera } from 'lucide-react';
import './LandingPage.css';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

const TAGS = [
  'Online Educators', 'Course Creators', 'Marketing Teams', 'Content Creators',
  'Sales Teams', 'Consultants', 'Training Managers', 'YouTubers', 'Teachers',
  'Coaches', 'Business Owners', 'Podcasters', 'Agencies', 'Nonprofits',
  'Researchers', 'Startups', 'HR Teams', 'Product Managers', 'Freelancers',
  'Event Speakers', 'Influencers',
];

const STEPS = [
  { number: '01', title: 'Write', body: 'Describe the presentation in plain language — topic, audience, length.' },
  { number: '02', title: 'Compose', body: 'Slideprintz lays out every slide with real photography, never AI-generated art.' },
  { number: '03', title: 'Print', body: 'Download a clean, fully editable PowerPoint or a set of HD PNG images.' },
];

const COMPARISON = [
  { label: 'Look & Feel', old: 'Obviously AI-generated', ours: 'Looks human-made' },
  { label: 'File Integrity', old: 'Exports break or glitch in PowerPoint', ours: 'Clean PPTX & PNG exports that open perfectly, every time' },
  { label: 'Photography', old: 'AI-generated — warped hands, plastic faces', ours: 'Real photography, HD' },
  { label: 'Templates', old: 'One rigid template, no options', ours: '60+ templates to choose from — no regeneration required' },
  { label: 'Text Editing', old: "Locked text you can't touch", ours: 'Edit any text or bullet point directly' },
  { label: 'Your Images', old: 'Stuck with generated images', ours: 'Upload your own — replace any slide image' },
  { label: 'Export', old: 'A flattened picture pretending to be a slide', ours: 'True PPTX — real text boxes & layout' },
  { label: 'Formats', old: 'One clunky, locked-down PDF', ours: 'Clean editable PPTX + a full HD PNG set' },
  { label: 'Speed', old: 'Slow, multi-minute generation', ours: 'Full deck generated in under 60 seconds' },
];

const PLANS = [
  {
    name: 'Free', tagline: 'Try AI-powered presentation creation', price: '$0',
    features: [
      '5 Prompt-Based Projects per Month',
      'Real HD Photography in All Templates',
      'Unlimited PNG & PPTX Exports'
    ],
    support: 'Community support', cta: 'Start Free', ctaLink: '',
  },
  {
    name: 'Starter', tagline: 'For individual creators', price: '$7', priceInterval: '/month',
    features: [
      '70 Prompt-Based Projects per Month',
      'Real HD Photography in All Templates',
      'Unlimited PNG & PPTX Exports'
    ],
    support: 'Email support', cta: 'See All Features', ctaLink: '/pricing-plans',
  },
  {
    name: 'Creator', tagline: 'For educators and content professionals', price: '$15', priceInterval: '/month',
    features: [
      '200 Prompt-Based Projects per Month',
      'Real HD Photography in All Templates',
      'Unlimited PNG & PPTX Exports',
      'Priority Rendering Queue'
    ],
    support: 'Priority support', cta: 'See All Features', ctaLink: '/pricing-plans', highlight: true,
  },
  {
    name: 'Professional', tagline: 'For teams and course creators', price: '$29', priceInterval: '/month',
    features: [
      '350 Prompt-Based Projects per Month',
      'Real HD Photography in All Templates',
      'Unlimited PNG & PPTX Exports',
      'Priority Support',
      'Priority Rendering Queue'
    ],
    support: 'Dedicated support', cta: 'See All Features', ctaLink: '/pricing-plans',
  },
];

const FAQS = [
  { q: 'What is Slideprintz?', a: 'An AI platform that turns text prompts into presentations — built with real photography instead of AI-generated images, and clean exports instead of flattened slides.' },
  { q: 'Are the images AI-generated?', a: 'No. Every image is real, professional photography — no uncanny AI faces, warped hands, or plastic-looking stock art. Want a different shot? Swap it in one click and it stays in the same spot.' },
  { q: 'Will the PowerPoint file actually be editable?', a: "Yes. Exports are real PPTX files with genuine text boxes, layouts, and image placeholders — not a flattened picture pretending to be a slide. Open it in PowerPoint and everything is exactly where you'd expect, fully editable." },
  { q: 'Do I need design skills?', a: 'No. Slideprintz handles layout, formatting, and hierarchy. You provide the content, we handle the rest.' },
  { q: 'How does it work?', a: 'Describe your presentation in text (30–60 seconds) and Slideprintz generates a full set of slides to review and export.' },
  { q: 'What export formats are available?', a: 'A clean, editable PowerPoint (PPTX) or a full set of HD PNG images — both unlimited on paid plans.' },
  { q: 'Can I edit the generated slides?', a: 'Swap in your own images or replace any photo in place, and pick a design before exporting. Export as PPTX to edit text, layout, and content directly in PowerPoint.' },
  { q: 'How do I write effective prompts?', a: 'Be specific. "Create a 10-slide pitch deck about renewable energy for investors" works better than "slides about solar".' },
  { q: 'Can I paste existing content?', a: 'Yes. Paste articles, notes, or outlines into the prompt and Slideprintz will structure it into slide breaks.' },
  { q: 'Are my presentations saved?', a: 'Every generation is saved automatically. Open "My Presentations" anytime to view or download past work.' },
  { q: 'Need help or have questions?', a: 'Contact Support@slideprintz.com or use the chat widget. We typically respond within 1 hour during business hours.' },
];

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { user, showAuthModal } = useAuth();
  const [pendingRedirect, setPendingRedirect] = useState(false);
  const [pendingState, setPendingState] = useState(null);
  const navigate = useNavigate();

  const howItWorksRef = useRef(null);

  const [heroText, setHeroText] = useState('');
  const heroTextareaRef = useRef(null);

  const handleWatchDemoClick = () => {
    if (howItWorksRef.current) {
      howItWorksRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (user && pendingRedirect && pendingState) {
      navigate('/generate', { state: pendingState });
      setPendingRedirect(false);
      setPendingState(null);
    }
  }, [user, pendingRedirect, pendingState, navigate]);

  const handleGetStartedClick = () => {
    if (user) { navigate('/generate'); }
    else { setPendingRedirect(true); showAuthModal('signup'); }
  };

  const handleHeroSubmit = () => {
    const text = heroText.trim();
    if (!text) return;
    const state = { heroPrompt: text, autoSubmit: true };
    if (user) {
      navigate('/generate', { state });
    } else {
      setPendingState(state);
      setPendingRedirect(true);
      showAuthModal('signup');
    }
  };

  const handleHeroKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleHeroSubmit(); }
  };

  const canSubmitHero = heroText.trim().length > 0;

  useEffect(() => {
    if (heroTextareaRef.current) {
      heroTextareaRef.current.style.height = 'auto';
      heroTextareaRef.current.style.height = Math.min(heroTextareaRef.current.scrollHeight, 180) + 'px';
    }
  }, [heroText]);

  return (
    <div className="landing-page">

      {/* HERO */}
      <section className={`hero-section ${isVisible ? 'visible' : ''}`}>
        <div className="hero-container">
          <div className="hero-left">
           

            <h1 className="hero-title">
              AI Presentations That Look
           
              <span className="hero-title-accent"> Human-Made.</span>
            </h1>

            <div className="hero-prompt-box">
              <div className="hero-textarea-wrapper">
                <textarea
                  ref={heroTextareaRef}
                  className="hero-prompt-textarea"
                  placeholder="Describe your slides..."
                  value={heroText}
                  onChange={(e) => {
                    setHeroText(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 180) + 'px';
                  }}
                  onKeyDown={handleHeroKeyDown}
                  rows={1}
                  maxLength={5000}
                />
                <div className="hero-textarea-actions">
                  <button
                    className="hero-send-btn"
                    onClick={handleHeroSubmit}
                    disabled={!canSubmitHero}
                    title="Generate"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 19V5M5 12l7-7 7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <p className="hero-subtitle">
              Describe your topic and Slideprintz generates a complete presentation in under 60 seconds, using real images instead of AI-generated images. Export a fully editable PowerPoint file or a set of HD PNG images.
            </p>

            <div className="hero-actions">
              <button className="btn-cta-solid" onClick={handleGetStartedClick}>
                <Sparkles className="btn-icon" />
                Start printing free
              </button>
              <button className="btn-cta-outline" onClick={handleWatchDemoClick}>
                <Play className="btn-icon" />
                Watch Demo
              </button>
            </div>
          </div>

          <div className="hero-right">
            <img
              src="/herodemo.png"
              alt="Slideprintz presentation demo"
              className="hero-demo-image"
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <div className="how-section" ref={howItWorksRef}>
        <span className="eyebrow eyebrow-center">THE PROCESS</span>
        <h2 className="section-title">From prompt to printed deck</h2>

        <div className="process-strip">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.number}>
              <div className="process-step">
                <span className="process-mark">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
              {i < STEPS.length - 1 && <span className="process-divider" aria-hidden="true" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* WHO IT'S FOR + COMPARISON */}
      <div className="who-section">
        <h2 className="section-title">Built for anyone who has to turn ideas into slides</h2>

        <div className="tag-rail">
          {TAGS.map((tag) => (
            <span className="tag-pill" key={tag}>{tag}</span>
          ))}
        </div>

        <div className="compare-table">
          <div className="compare-header">
            <span className="compare-header-cell" />
            <span className="compare-header-cell compare-header-old">Typical AI slides</span>
            <span className="compare-header-cell compare-header-ours">Slideprintz</span>
          </div>
          {COMPARISON.map((row) => (
            <div className="compare-row" key={row.label}>
              <span className="compare-label">{row.label}</span>
              <span className="compare-cell compare-old">
                <X size={14} strokeWidth={2.5} className="compare-icon-x" />
                {row.old}
              </span>
              <span className="compare-cell compare-ours">
                <Check size={14} strokeWidth={2.5} className="compare-icon-check" />
                {row.ours}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* PRICING */}
      <section className="pricing-section">
        <h2 className="section-title">Simple, transparent pricing</h2>
        <p className="section-subtitle">Pay only for what you need. Choose the plan that fits.</p>
        <div className="pricing-grid-4">
          {PLANS.map((plan, i) => (
            <div key={i} className={`pricing-card ${plan.highlight ? 'pricing-card-highlight' : ''}`}>
              {plan.highlight && <div className="popular-stamp">Most printed</div>}
              <h3 className="plan-name">{plan.name}</h3>
              <p className="plan-tagline">{plan.tagline}</p>
              <div className="plan-price-row">
                <span className="plan-price">{plan.price}</span>
                {plan.priceInterval && <span className="plan-price-interval">{plan.priceInterval}</span>}
              </div>
              <ul className="plan-features">
                {plan.features.map((f, j) => (
                  <li key={j} className="plan-feature-item">
                    <span className="plan-check"><Check size={12} color="#FFFFFF" strokeWidth={3} /></span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="plan-support">Support: {plan.support}</p>
              <button className="plan-cta" onClick={() => { navigate(plan.ctaLink); window.scrollTo(0, 0); }}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <h2 className="section-title">Frequently asked questions</h2>
        <p className="section-subtitle">Everything you need to know about Slideprintz</p>
        <div className="faq-container">
          {FAQS.map((item, i) => (
            <div className="faq-card" key={i}>
              <span className="faq-index">N&deg;{String(i + 1).padStart(2, '0')}</span>
              <h3 className="faq-title">{item.q}</h3>
              <p className="faq-desc">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final-cta">
        <div className="final-cta-inner">
          <span className="eyebrow-stamp eyebrow-stamp-dark">
            <Camera size={13} strokeWidth={2.2} />
            Ready when you are
          </span>
          <h2 className="final-cta-title">Ready to print your next deck?</h2>
          <p className="final-cta-subtitle">Real photography, clean exports, no AI slop — professional presentations in seconds, not hours.</p>
          <button className="final-cta-button" onClick={handleGetStartedClick}>Start printing free</button>
          <p className="final-cta-note">No credit card required · 5 free projects to start</p>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;